import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, Lock, Download, ArrowUpRight } from "lucide-react";
import { fetchArticles } from "../lib/api";
import { ArticleCard } from "../components/ArticleCard";
import { useAuth } from "../context/AuthContext";
import { Seo } from "../components/Seo";

export default function KnowledgeBase() {
  const [articles, setArticles] = useState([]);
  const { user } = useAuth();

  useEffect(() => {
    fetchArticles().then(setArticles).catch(() => {});
  }, []);

  return (
    <div data-testid="knowledge-base-page" className="max-w-[1360px] mx-auto px-6 lg:px-10 py-16">
      <Seo title="Peptide Research Knowledge Base" description="In-depth research guides on BPC-157, NAD+, MOTS-c, Glutathione and other peptides — handling, storage, reconstitution and literature summaries." />
      <div className="max-w-3xl">
        <div className="label-mono">Knowledge Base</div>
        <h1 className="mt-4 text-4xl lg:text-6xl tracking-tight text-white font-medium">
          In-depth research, plainly written.
        </h1>
        <p className="mt-4 text-[#B7BCC7] max-w-xl">
          Peer-informed guides authored by our lab team. Mechanisms, storage,
          reconstitution and laboratory practice — no marketing gloss.
        </p>
      </div>

      {/* Featured resource callout */}
      <Link
        to="/downloads"
        data-testid="kb-featured-resource"
        className="mt-12 grid grid-cols-1 lg:grid-cols-12 items-center gap-8 group border border-[#1565FF]/40 rounded-3xl bg-[#0e0e0e] hover:bg-[#111] transition-colors overflow-hidden shadow-lab-glow"
      >
        <div className="lg:col-span-4 relative h-full min-h-[220px]">
          <div className="absolute inset-0 grid-backdrop opacity-30" />
          <div className="absolute inset-6 rounded-2xl border border-[#2A2A2A] bg-[#0B0B0B] p-6 flex flex-col">
            <div className="label-mono">Peptide Labs N.I.</div>
            <div className="mt-4 text-white text-xl tracking-tight leading-tight font-medium">
              Advanced <br /> Peptide<br /> Protocols
            </div>
            <div className="mt-auto pt-6 flex items-center justify-between text-[10px] font-mono text-[#6E7784]">
              <span>REV 2026.02</span>
              <span>8 PAGES</span>
            </div>
          </div>
          <div className="absolute top-6 right-6 inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-[#0B0B0B]/80 border border-[#1565FF]/40">
            <FileText size={11} className="text-[#1565FF]" />
            <span className="text-[10px] font-mono tracking-widest text-white">PDF</span>
          </div>
        </div>
        <div className="lg:col-span-8 p-8 lg:pr-12">
          <div className="label-mono flex items-center gap-3">
            Featured resource
            {!user ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#1565FF] text-white text-[10px] tracking-widest">
                <Lock size={10} /> MEMBER
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#1565FF]/15 border border-[#1565FF]/40 text-[#1565FF] text-[10px] tracking-widest">
                <Download size={10} /> UNLOCKED
              </span>
            )}
          </div>
          <h2 className="mt-3 text-3xl lg:text-4xl tracking-tight text-white font-medium">
            Advanced Peptide Protocols
          </h2>
          <p className="mt-3 text-[#B7BCC7] leading-relaxed max-w-xl">
            Storage, reconstitution, injection technique, dose-calculation and compound-specific notes for
            every compound in our catalog — free for verified researchers.
          </p>
          <span className="mt-6 inline-flex items-center gap-2 text-white group-hover:text-[#1565FF] transition-colors">
            {user ? "Download PDF" : "Sign in to unlock"} <ArrowUpRight size={16} />
          </span>
        </div>
      </Link>

      <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {articles.map((a) => (
          <ArticleCard key={a.id} article={a} />
        ))}
      </div>
    </div>
  );
}
